import React, { useCallback, useState } from "react";
import { conditionallyCreateStyleObj, getClassConditionalAppend, getColors, getContainerClassPerMaxWidth, isValidCountry, isValidEmail, isValidName, isValidPhone, } from "../../utils";
import { ConditionalPortal } from "../Cards/VideoCardDeck/SimpleLightbox/ConditionalPortal";
import { Container, ContainerFW } from "../global";
import { Heading } from "../Heading";
import { FormInputs, } from "./FormInputs/FormInputs";
import { Overlay } from "./Overlay/Overlay";
const initialUserData = {
    name: {
        value: "",
        isValid: false,
    },
    email: {
        value: "",
        isValid: false,
    },
    phoneNumber: {
        value: "",
        isValid: false,
    },
    country: {
        value: "",
        isValid: false,
    },
    "m35-agreementCheckbox": {
        value: "",
        isValid: true,
    },
};
const initialErrorData = {
    email: undefined,
    phoneNumber: undefined,
    name: undefined,
    country: undefined,
    "m35-agreementCheckbox": undefined,
};
export const M35IntouchForm = ({ subTitle, title, userData, buttonLabel, titleAlignment, titleColor, subTitleColor, subTitleAlignment, backgroundColor, containerMaxWidth, backgroundImageUrl, emailErrorMessage, phoneErrorMessage, nameErrorMessage, countryErrorMessage, agreementCheckboxErrorPlaceholder, agreementCheckboxIsMandatory, modalMessage, modalButtonMessage, modalButtonUrl, campaignId, errorModalMessage, sendingInfoMessage, formHasLabels = false, disclaimerText, inputBackgroundColor, inputBorderColor, labelColor, formBoxStyle, isButtonFullWidth = false, }) => {
    const hasCountryField = Array.isArray(userData.countriesList) && userData.countriesList.length > 0;
    if (!hasCountryField) {
        initialUserData.country.isValid = true;
    }
    const [formData, setFormData] = React.useState(initialUserData);
    const [isLoading, setIsLoading] = useState(false);
    const [errorValue, setErrorValue] = useState(initialErrorData);
    const [currentModalMessage, setCurrentModalMessage] = useState(modalMessage);
    const { bgColor } = getColors(backgroundColor);
    const containerMaxWidthClass = getContainerClassPerMaxWidth(containerMaxWidth);
    const containerExtraClass = getClassConditionalAppend(containerMaxWidthClass);
    const getExtraRootClasses = () => {
        let extraClasses = "";
        if (formBoxStyle?.borderColor) {
            extraClasses += "  in-touch-form-root--with-border";
        }
        return extraClasses;
    };
    const handleCheck = useCallback((htmlId, value) => {
        let validated;
        if (htmlId === "m35-agreementCheckbox") {
            validated = true;
            setErrorValue((prev) => ({
                ...prev,
                "m35-agreementCheckbox": undefined,
            }));
            setFormData((prevData) => ({
                ...prevData,
                "m35-agreementCheckbox": {
                    value,
                    isValid: true,
                },
            }));
            return validated;
        }
        if (htmlId === "name") {
            validated = isValidName(value);
            if (!validated) {
                setErrorValue((prev) => ({
                    ...prev,
                    name: nameErrorMessage,
                }));
                return validated;
            }
            setErrorValue((prev) => ({ ...prev, name: undefined }));
            return validated;
        }
        if (htmlId === "email") {
            validated = isValidEmail(value);
            if (!validated) {
                setErrorValue((prev) => ({
                    ...prev,
                    email: emailErrorMessage,
                }));
                return validated;
            }
            setErrorValue((prev) => ({ ...prev, email: undefined }));
            return validated;
        }
        if (htmlId === "phoneNumber") {
            validated = isValidPhone(value);
            if (!validated) {
                setErrorValue((prev) => ({
                    ...prev,
                    phoneNumber: phoneErrorMessage,
                }));
                return validated;
            }
            setErrorValue((prev) => ({ ...prev, phoneNumber: undefined }));
            return validated;
        }
        if (htmlId === "country") {
            validated = isValidCountry(value, userData.countriesList);
            if (!validated) {
                setErrorValue((prev) => ({
                    ...prev,
                    country: countryErrorMessage,
                }));
                return validated;
            }
            setErrorValue((prev) => ({ ...prev, country: undefined }));
            return validated;
        }
    }, [countryErrorMessage, emailErrorMessage, nameErrorMessage, phoneErrorMessage, userData.countriesList]);
    const handleUserInput = useCallback((event) => {
        const value = event.target.value;
        const htmlId = event.target.id;
        const correctedValue = event.target.type === "checkbox"
            ? event.target.checked
                ? value
                : ""
            : value;
        const isValid = handleCheck(htmlId, value);
        setFormData((prevData) => ({
            ...prevData,
            [htmlId]: {
                value: correctedValue,
                isValid,
            },
        }));
    }, [handleCheck]);
    const constructRequestFormData = React.useCallback(() => {
        const requestFormData = new FormData();
        for (const key in formData) {
            if (key !== "formFile") {
                requestFormData.append(key, formData[key].value);
            }
            else {
                const files = formData[key];
                files.forEach((file) => {
                    requestFormData.append("formFile", file);
                });
            }
        }
        campaignId && requestFormData.append("campaignId", campaignId);
        return requestFormData;
    }, [campaignId, formData]);
    const getResponseMessage = useCallback(async (response) => {
        if (response.status === 200) {
            return modalMessage;
        }
        if (response.status === 409) {
            try {
                const json = await response.json();
                return json.message?.value || errorModalMessage;
            }
            catch (error) {
                console.error("Failed to parse JSON:", error);
                return errorModalMessage;
            }
        }
        return errorModalMessage;
    }, [errorModalMessage, modalMessage]);
    const sendRequest = React.useCallback(async () => {
        setIsLoading(true);
        setCurrentModalMessage(sendingInfoMessage);
        const requestFormData = constructRequestFormData();
        try {
            const r = await fetch("/m35intouchform", {
                method: "POST",
                body: requestFormData,
            });
            const modalMessage = await getResponseMessage(r);
            setCurrentModalMessage(modalMessage);
        }
        catch {
            setCurrentModalMessage(errorModalMessage);
        }
    }, [constructRequestFormData, errorModalMessage, getResponseMessage, sendingInfoMessage]);
    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (isLoading) {
            return;
        }
        let canSubmit = true;
        const inputValues = Object.keys(formData);
        inputValues.forEach((fieldName) => {
            if (!formData[fieldName].isValid) {
                canSubmit = false;
            }
        });
        inputValues.forEach((a) => handleCheck(a, formData[a].value));
        if (!canSubmit) {
            return;
        }
        if (canSubmit) {
            await sendRequest();
        }
        setFormData(initialUserData);
        setErrorValue(initialErrorData);
    }, [formData, handleCheck, isLoading, sendRequest]);
    const handleModalVisibility = useCallback(() => {
        setIsLoading(!isLoading);
    }, [isLoading]);
    const handleErrorValidator = useCallback((event) => {
        const value = event.target.value;
        const htmlId = event.target.id;
        handleCheck(htmlId, value);
    }, [handleCheck]);
    const getCustomStyleObj = () => conditionallyCreateStyleObj({
        "--background-image-url": `url(${backgroundImageUrl})`,
        "--input-bkgr-color": inputBackgroundColor,
        "--input-border-color": inputBorderColor,
        "--label-color": labelColor,
        "--form-background-color": formBoxStyle?.backgroundColor,
        "--form-border-color": formBoxStyle?.borderColor,
        "--form-border-radius": formBoxStyle?.borderRadius
            ? `${formBoxStyle?.borderRadius}px`
            : undefined,
        "--form-padding": formBoxStyle?.padding
            ? `${formBoxStyle?.padding}px`
            : undefined,
    });
    const customStyleObj = getCustomStyleObj();
    return (React.createElement("div", { className: `in-touch-form-root ${getExtraRootClasses()}`, style: customStyleObj, id: "M35-Intouch-Form-identifier" },
        React.createElement(ContainerFW, { className: containerExtraClass, backgroundColor: bgColor },
            React.createElement(Container, { className: containerExtraClass },
                React.createElement("div", { className: "in-touch-form" },
                    isLoading && (React.createElement(ConditionalPortal, { isActivePortal: true },
                        React.createElement(Overlay, { handleVisibility: handleModalVisibility, modalMessage: currentModalMessage, modalButtonMessage: modalButtonMessage, modalButtonUrl: modalButtonUrl }))),
                    React.createElement("div", { className: "in-touch-form__textWrapper" },
                        title && (React.createElement(Heading, { className: "h2-regular", style: {
                                "--title-text-alignment": titleAlignment,
                                "--title-text-color": titleColor,
                            } }, title)),
                        subTitle && (React.createElement("p", { className: "in-touch-form-subTitle", style: {
                                "--subTitle-text-alignment": subTitleAlignment,
                                "--subTitle-text-color": subTitleColor,
                            } }, subTitle))),
                    React.createElement(FormInputs, { errorMessages: errorValue, onSubmit: handleSubmit, name: formData.name.value, email: formData.email.value, phoneNumber: formData.phoneNumber.value, country: formData.country.value, agreementCheckboxChecked: !!formData["m35-agreementCheckbox"].value, emailPlaceholder: userData.emailPlaceholder, namePlaceholder: userData.namePlaceholder, phoneNumberPlaceholder: userData.phoneNumberPlaceholder, emailLabel: userData.emailLabel, nameLabel: userData.nameLabel, phoneNumberLabel: userData.phoneNumberLabel, hasCountryField: hasCountryField, countryLabel: userData.countryLabel, countriesList: userData.countriesList, agreementCheckboxLabel: userData.agreementCheckboxLabel, agreementCheckboxIsMandatory: agreementCheckboxIsMandatory, inputHandler: handleUserInput, buttonLabel: buttonLabel, validateErrors: handleErrorValidator, hasLabels: formHasLabels, disclaimerText: disclaimerText, isButtonFullWidth: isButtonFullWidth }))))));
};
